import React from "react";
import { Link } from "react-router-dom";
import {
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiMail,
  FiMapPin,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { isCreativeUserRecord } from "../utils/buttonVisibility";

const Profile = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const isCreative = isCreativeUserRecord(user);
  const displayName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ");
  const initials =
    [user?.firstName, user?.lastName]
      .filter(Boolean)
      .map((value) => value.charAt(0))
      .join("")
      .toUpperCase() || "U";
  const roleLabel = [user?.position, user?.department || user?.team]
    .filter(Boolean)
    .join(" • ");

  const profileFields = [
    { label: "Username", value: user?.username, icon: <FiMail /> },
    { label: "Department", value: user?.department, icon: <FiBriefcase /> },
    { label: "Team", value: user?.team, icon: <FiUsers /> },
    { label: "Team Group", value: user?.teamGroup, icon: <FiMapPin /> },
    { label: "Position", value: user?.position, icon: <FiUser /> },
    { label: "Company", value: user?.company, icon: <FiBriefcase /> },
  ];

  if (!isCreative) {
    profileFields.splice(1, 0, {
      label: "Level",
      value: user?.level,
      icon: <FiCheckCircle />,
    });
  }

  const quickLinks = [
    {
      title: "Meeting Room Booking",
      description: "จองห้องประชุมและดูตารางใช้งาน",
      to: "/meeting-rooms",
      icon: <FiCalendar />,
    },
    {
      title: "Travel Expense",
      description: "ดูและจัดการคำขอเดินทางของคุณ",
      to: "/travel-expense",
      icon: <FiMapPin />,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff7ff] via-[#f7f3ff] to-[#eef0ff] px-4 py-6 transition-colors duration-300 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950 md:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {user ? (
          <>
            <section className="overflow-hidden rounded-2xl border border-fuchsia-100 bg-white shadow-sm dark:border-fuchsia-900/50 dark:bg-gray-900">
              <div className="bg-gradient-to-r from-fuchsia-600 via-purple-600 to-indigo-600 px-6 py-7 text-white">
                <div className="flex flex-col gap-5 md:flex-row md:items-center">
                  <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/20 text-xl font-semibold ring-1 ring-white/40">
                      {initials}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-fuchsia-100">
                        {isCreative ? "Profile Workspace" : "User Profile"}
                      </p>
                      <h1 className="mt-1 text-2xl font-semibold">
                        {displayName || user.username || "User"}
                      </h1>
                      <p className="mt-1 text-sm text-fuchsia-50">
                        {roleLabel || "Neonworks Team Member"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {profileFields.map((field) => (
                <div
                  key={field.label}
                  className="rounded-xl border border-fuchsia-100 bg-white p-5 shadow-sm dark:border-fuchsia-900/50 dark:bg-gray-900"
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 rounded-xl bg-fuchsia-50 p-2 text-fuchsia-700 dark:bg-fuchsia-950/50 dark:text-fuchsia-200">
                      {field.icon}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {field.label}
                      </p>
                      <p className="mt-1 break-words text-base font-semibold text-gray-900 dark:text-gray-100">
                        {field.value || "-"}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </section>

            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Quick Actions
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {quickLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="group rounded-xl border border-fuchsia-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-fuchsia-300 hover:shadow-md dark:border-fuchsia-900/50 dark:bg-gray-900 dark:hover:border-fuchsia-700"
                  >
                    <div className="flex items-center gap-3">
                      <span className="rounded-xl bg-fuchsia-50 p-3 text-fuchsia-700 transition group-hover:bg-fuchsia-100 dark:bg-fuchsia-950/50 dark:text-fuchsia-200">
                        {item.icon}
                      </span>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </>
        ) : (
          <div className="rounded-2xl border border-fuchsia-100 bg-white p-6 text-sm text-gray-600 shadow-sm dark:border-fuchsia-900/50 dark:bg-gray-900 dark:text-gray-400">
            ไม่พบข้อมูลผู้ใช้งานในระบบ
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
